import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  output,
  untracked,
  viewChildren,
} from '@angular/core';
import { DayPeriod } from '../../_core/date-picker.model';
import {
  HOURS_PER_DAY,
  HOURS_PER_HALF_DAY,
  MINUTES_PER_HOUR,
} from '../../_core/date-picker.constants';
import { hour12ToHour24, hour24ToHour12 } from '../../_core/value-parser';

export interface TimeSelection {
  hour24: number;
  minute: number;
}

interface TimeOption {
  value: number;
  label: string;
}

const pad = (value: number): string => String(value).padStart(2, '0');

function buildOptions(from: number, count: number, step: number): TimeOption[] {
  // The step arrives as a bare `number` input, and anything below 1 never advances the loop.
  const stride = Number.isFinite(step) && step >= 1 ? Math.floor(step) : 1;
  const options: TimeOption[] = [];
  for (let i = 0; i < count; i += stride) {
    options.push({ value: from + i, label: pad(from + i) });
  }
  return options;
}

/**
 * Scrollable columns instead of three `ng-select` dropdowns nested inside the picker's
 * own overlay. `minuteStep` turns a 60-row list into 12 or 4, which was most of what
 * made the old one unusable.
 *
 * Selection is a pure function of the inputs, which are themselves derived from the
 * same slots signal the text field renders from — so the list cannot show a different
 * time from the one in the input.
 */
@Component({
  selector: 'fr8bot-ui-date-picker-time-list',
  imports: [],
  templateUrl: './time-list.component.html',
  styleUrl: './time-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePickerTimeListUIComponent {
  readonly hour24 = input<number | null>(null);
  readonly minute = input<number | null>(null);
  readonly hour12Format = input(false);
  readonly hourStep = input(1);
  readonly minuteStep = input(1);
  readonly disabled = input(false);

  readonly timeChange = output<TimeSelection>();

  private readonly columns = viewChildren<ElementRef<HTMLElement>>('column');

  protected readonly hours = computed<TimeOption[]>(() =>
    this.hour12Format()
      ? buildOptions(1, HOURS_PER_HALF_DAY, this.hourStep())
      : buildOptions(0, HOURS_PER_DAY, this.hourStep()),
  );

  protected readonly minutes = computed<TimeOption[]>(() =>
    buildOptions(0, MINUTES_PER_HOUR, this.minuteStep()),
  );

  protected readonly periods: readonly DayPeriod[] = ['am', 'pm'];

  protected readonly selectedHour = computed(() => {
    const hour = this.hour24();
    if (hour === null) return null;
    return this.hour12Format() ? hour24ToHour12(hour).hour12 : hour;
  });

  protected readonly selectedPeriod = computed(() => {
    const hour = this.hour24();
    return hour === null ? null : hour24ToHour12(hour).dayPeriod;
  });

  constructor() {
    // Bring the selection into view once the columns have rendered.
    afterRenderEffect(() => {
      const rendered = this.columns();
      this.selectedHour();
      this.minute();

      untracked(() => {
        for (const column of rendered) {
          const selected = column.nativeElement.querySelector('[data-selected="true"]');
          selected?.scrollIntoView({ block: 'center' });
        }
      });
    });
  }

  protected pickHour(value: number): void {
    if (this.disabled()) return;
    const hour24 = this.hour12Format()
      ? hour12ToHour24(value, this.selectedPeriod() ?? DayPeriod.Am)
      : value;
    this.emit(hour24, this.minute() ?? 0);
  }

  protected pickMinute(value: number): void {
    if (this.disabled()) return;
    this.emit(this.hour24() ?? 0, value);
  }

  protected pickPeriod(period: DayPeriod): void {
    if (this.disabled()) return;
    const current = this.hour24();
    const hour12 = current === null ? HOURS_PER_HALF_DAY : hour24ToHour12(current).hour12;
    this.emit(hour12ToHour24(hour12, period), this.minute() ?? 0);
  }

  private emit(hour24: number, minute: number): void {
    this.timeChange.emit({ hour24, minute });
  }
}
